/**
 * VW Client Documents Service
 * Service for accessing the VW_Client_Documents view API endpoints
 */

import { BaseService } from './BaseService';
import { parseUTCDate, formatLocalDateTime, formatTimeAgo } from '../utils/dateUtils';

/**
 * Helper class for working with client documents
 */
export class VWClientDocumentsHelper {
    /**
     * Get file extension from document
     * @param {Object} document - Document object
     * @returns {string} File extension (lowercase)
     */
    static getFileExtension(document) {
        if (!document?.fileName) return '';
        const fileName = document.fileName;
        if (!fileName.includes('.')) return '';
        return fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    }

    /**
     * Get file name without extension
     * @param {Object} document - Document object
     * @returns {string} File name without extension
     */
    static getFileNameWithoutExtension(document) {
        if (!document?.fileName) return '';
        const fileName = document.fileName;
        if (!fileName.includes('.')) return fileName;
        return fileName.substring(0, fileName.lastIndexOf('.'));
    }

    /**
     * Check if document has been modified
     * @param {Object} document - Document object
     * @returns {boolean} True if modified
     */
    static isModified(document) {
        if (!document?.createdAt || !document?.lastModifiedAt) return false;
        return document.createdAt !== document.lastModifiedAt;
    }

    /**
     * Get storage path for document
     * @param {Object} document - Document object
     * @returns {string} Storage path
     */
    static getStoragePath(document) {
        if (!document?.container || !document?.fileName) return '';
        return `${document.container}/${document.fileName}`;
    }

    /**
     * Format date for display
     * @param {string} dateString - UTC date string from backend
     * @returns {string} Formatted date in local time
     */
    static formatDate(dateString) {
        if (!dateString) return 'N/A';
        return formatLocalDateTime(dateString);
    }

    /**
     * Get relative time (e.g., "2 hours ago")
     * @param {string} dateString - UTC date string from backend
     * @returns {string} Relative time string
     */
    static getRelativeTime(dateString) {
        if (!dateString) return 'Unknown';
        return formatTimeAgo(dateString);
    }

    /**
     * Get file icon type based on extension
     * Returns the icon type string, not the component itself
     * @param {Object} document - Document object
     * @returns {string} Icon type identifier
     */
    static getFileIconType(document) {
        const ext = this.getFileExtension(document);
        
        // Icon type mapping for common file extensions
        const iconMap = {
            // PDF
            'pdf': 'pdf',
            
            // Microsoft Word
            'doc': 'word',
            'docx': 'word',
            
            // Microsoft Excel
            'xls': 'excel',
            'xlsx': 'excel',
            'xlsm': 'excel',
            'csv': 'excel',
            
            // Microsoft PowerPoint
            'ppt': 'powerpoint',
            'pptx': 'powerpoint',
            
            // Images
            'jpg': 'image',
            'jpeg': 'image',
            'png': 'image',
            'gif': 'image',
            'bmp': 'image',
            'svg': 'image',
            'webp': 'image',
            
            // Archives
            'zip': 'archive',
            'rar': 'archive',
            '7z': 'archive',
            'tar': 'archive',
            'gz': 'archive',
            
            // Text/Code
            'txt': 'text',
            'log': 'text',
            'md': 'text',
            'json': 'code',
            'xml': 'code',
            'html': 'code',
            'css': 'code',
            'js': 'code',
            'jsx': 'code',
            'ts': 'code',
            'tsx': 'code',
            'py': 'code',
            'java': 'code',
            
            // Audio
            'mp3': 'audio',
            'wav': 'audio',
            'flac': 'audio',
            'aac': 'audio',
            
            // Video
            'mp4': 'video',
            'avi': 'video',
            'mov': 'video',
            'wmv': 'video',
            'mkv': 'video'
        };
        
        return iconMap[ext] || 'file';
    }

    /**
     * Group documents by container
     * @param {Array} documents - Array of documents
     * @returns {Object} Documents grouped by container
     */
    static groupByContainer(documents) {
        if (!Array.isArray(documents)) return {};
        return documents.reduce((acc, doc) => {
            const container = doc.container || 'Unknown';
            if (!acc[container]) {
                acc[container] = [];
            }
            acc[container].push(doc);
            return acc;
        }, {});
    }

    /**
     * Group documents by date (day)
     * @param {Array} documents - Array of documents
     * @returns {Object} Documents grouped by date in local time
     */
    static groupByDate(documents) {
        if (!Array.isArray(documents)) return {};
        return documents.reduce((acc, doc) => {
            if (!doc.createdAt) return acc;
            const parsedDate = parseUTCDate(doc.createdAt);
            if (!parsedDate) return acc;
            const date = parsedDate.toLocaleDateString();
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(doc);
            return acc;
        }, {});
    }

    /**
     * Filter documents by file extension
     * @param {Array} documents - Array of documents
     * @param {Array} extensions - Array of extensions to filter by
     * @returns {Array} Filtered documents
     */
    static filterByExtension(documents, extensions) {
        if (!Array.isArray(documents) || !Array.isArray(extensions)) return documents || [];
        const lowerExtensions = extensions.map(ext => ext.toLowerCase());
        return documents.filter(doc => {
            const ext = this.getFileExtension(doc);
            return lowerExtensions.includes(ext);
        });
    }

    /**
     * Sort documents
     * @param {Array} documents - Array of documents
     * @param {string} sortBy - Sort field ('name', 'created', 'modified')
     * @param {string} order - Sort order ('asc' or 'desc')
     * @returns {Array} Sorted documents
     */
    static sortDocuments(documents, sortBy = 'created', order = 'desc') {
        if (!Array.isArray(documents)) return [];
        const sorted = [...documents];
        sorted.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'name':
                    comparison = (a.fileName || '').localeCompare(b.fileName || '');
                    break;
                case 'created': {
                    const dateA = parseUTCDate(a.createdAt);
                    const dateB = parseUTCDate(b.createdAt);
                    const timeA = dateA ? dateA.getTime() : 0;
                    const timeB = dateB ? dateB.getTime() : 0;
                    comparison = timeA - timeB;
                    break;
                }
                case 'modified': {
                    const dateA = parseUTCDate(a.lastModifiedAt);
                    const dateB = parseUTCDate(b.lastModifiedAt);
                    const timeA = dateA ? dateA.getTime() : 0;
                    const timeB = dateB ? dateB.getTime() : 0;
                    comparison = timeA - timeB;
                    break;
                }
            }
            return order === 'asc' ? comparison : -comparison;
        });
        return sorted;
    }
}

/**
 * VW Client Documents Service Class
 */
export class VWClientDocumentsService extends BaseService {
    constructor() {
        super('/api/client-documents', ['createdAt', 'lastModifiedAt']);
    }

    /**
     * Get all client documents ordered by creation date (newest first)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of documents
     */
    async getDocumentsOrderedByCreatedDate(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/order-by-created-date');
        } catch (error) {
            console.error('Failed to fetch documents ordered by created date:', error);
            throw error;
        }
    }

    /**
     * Get all client documents ordered by modified date (newest first)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of documents
     */
    async getDocumentsOrderedByModifiedDate(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/order-by-modified-date');
        } catch (error) {
            console.error('Failed to fetch documents ordered by modified date:', error);
            throw error;
        }
    }

    /**
     * Get all documents for a specific client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of documents
     */
    async getDocumentsByClientId(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}`);
        } catch (error) {
            console.error(`Failed to fetch documents for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get client documents ordered by creation date
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {boolean} bypassCache - Whether to bypass cache (default: false)
     * @returns {Promise<Array>} Array of documents
     */
    async getDocumentsByClientIdOrderedByDate(accessToken, clientId, bypassCache = false) {
        try {
            const endpoint = `/client/${clientId}/order-by-created-date${bypassCache ? `?_t=${Date.now()}` : ''}`;
            const options = bypassCache ? {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            } : {};
            return await this.makeRequest(accessToken, endpoint, options);
        } catch (error) {
            console.error(`Failed to fetch ordered documents for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get documents in a specific container
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @returns {Promise<Array>} Array of documents
     */
    async getDocumentsByContainer(accessToken, container) {
        try {
            return await this.makeRequest(accessToken, `/container/${encodeURIComponent(container)}`);
        } catch (error) {
            console.error(`Failed to fetch documents for container ${container}:`, error);
            throw error;
        }
    }

    /**
     * Search documents by file name
     * @param {string} accessToken - JWT access token
     * @param {string} fileName - File name to search
     * @returns {Promise<Array>} Array of documents
     */
    async searchDocumentsByFileName(accessToken, fileName) {
        try {
            return await this.makeRequest(accessToken, `/search?fileName=${encodeURIComponent(fileName)}`);
        } catch (error) {
            console.error(`Failed to search documents by file name ${fileName}:`, error);
            throw error;
        }
    }

    /**
     * Get all distinct containers
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of container names
     */
    async getAllContainers(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/containers');
        } catch (error) {
            console.error('Failed to fetch containers:', error);
            throw error;
        }
    }
}

// Create singleton instance
const vwClientDocumentsService = new VWClientDocumentsService();

// Export both named and default for flexibility
export { vwClientDocumentsService };
export default vwClientDocumentsService;
